import { IllegalArgumentError } from "@archipad-js/core/error";
import { SHA1, SHA512 } from "crypto-js";

function randHex(max) {
    return ("000" + Math.floor(Math.random() * max).toString(16)).substr(-4, 4);
}

export function hexStringToBigInt(hexStr: string): bigint {
    return BigInt(`0x${hexStr}`);
}

export function hexStringToDecimalString(hexStr: string): string {
    return hexStringToBigInt(hexStr).toString(10);
}

function generateStableHash63(deterministicKey: string): bigint {
    const key = SHA1(deterministicKey);
    const hash1 = (key.words[key.words.length - 2] >>> 0) & 0x7fffffff;
    const hash2 = key.words[key.words.length - 1] >>> 0;

    const hash = [hash1, hash2]
        .map((hash) => BigInt(hash.toString()))
        .reduce((previous, current) => (previous << BigInt(32)) + current, BigInt(0));

    return hash;
}

function generateStableHash128(deterministicKey: string): bigint {
    const key = SHA1(deterministicKey);
    const hash1 = key.words[key.words.length - 4] >>> 0;
    const hash2 = key.words[key.words.length - 3] >>> 0;
    const hash3 = key.words[key.words.length - 2] >>> 0;
    const hash4 = key.words[key.words.length - 1] >>> 0;

    const hash = [hash1, hash2, hash3, hash4]
        .map((hash) => BigInt(hash.toString()))
        .reduce((previous, current) => (previous << BigInt(32)) + current);

    return hash;
}

function generateDeterministicIdWithStringKey(baseDeterministicId: string, deterministicKey: string) {
    const baseId = BigInt(baseDeterministicId);
    const hash = generateStableHash63(deterministicKey);

    return (baseId ^ hash).toString();
}

function generateDeterministicIdWithNumericKey(baseDeterministicId: string, deterministicKey: string) {
    const baseId = BigInt(baseDeterministicId);
    const keyId = BigInt(deterministicKey);
    return (baseId ^ keyId).toString();
}

function generateDeterministicIdWithUUIDKey(baseDeterministicId: string, deterministicKey: string) {
    if (!deterministicKey.match(/^[A-Z0-9]{32}$/))
        throw new IllegalArgumentError("Invalid UUID key", "deterministicKey", "UUID", deterministicKey);
    const keyStrLow = deterministicKey.substring(16);
    let keyId = hexStringToBigInt(keyStrLow);
    keyId = keyId & BigInt("0x7FFFFFFFFFFFFFFF"); // force sign bit to 0

    const baseId = BigInt(baseDeterministicId);
    return (baseId ^ keyId).toString();
}

//------------------------------------------------------------------------------
export function generateId(): string {
    // generate an int64 random id (in 16 bits chunk to workaround rand generator issues on some browsers)
    const str = randHex(0x7fff) + randHex(0xffff) + randHex(0xffff) + randHex(0xffff);

    // convert to decimal and return
    const id = hexStringToDecimalString(str);
    return id;
}

export type DeterministicIdKey = "string" | "number" | "uuid";

//
export function generateDeterministicId(
    baseDeterministicId: string,
    deterministicKey: string,
    keyType: DeterministicIdKey = "string",
) {
    if (typeof baseDeterministicId !== "string" || !baseDeterministicId.match(/^[0-9]+$/))
        throw new IllegalArgumentError(
            "Invalid baseDeterministicId key",
            "baseDeterministicId",
            "DeterministicId",
            baseDeterministicId,
        );
    if (typeof deterministicKey !== "string")
        throw new IllegalArgumentError(
            "Invalid deterministicKey key",
            "deterministicKey",
            "DeterministicKey",
            deterministicKey,
        );

    if (keyType == "uuid") {
        return generateDeterministicIdWithUUIDKey(baseDeterministicId, deterministicKey);
    } else if (keyType === "number") {
        return generateDeterministicIdWithNumericKey(baseDeterministicId, deterministicKey);
    } else {
        return generateDeterministicIdWithStringKey(baseDeterministicId, deterministicKey);
    }
}

export function generateDeterministicTemplateIdFromTemplateIdAndEntityId(
    baseTemplateId: string,
    rootEntityId: string,
): string {
    const hexId = (hexStringToBigInt(baseTemplateId) ^ BigInt(rootEntityId)).toString(16).toUpperCase();
    return ("00000000000000000000000000000000" + hexId).slice(-32);
}

export function generateDeterministicTemplateIdFromTemplateIdAndTemplateId(
    templateId1: string,
    templateId2: string,
): string {
    const hexId = (hexStringToBigInt(templateId1) ^ hexStringToBigInt(templateId2)).toString(16).toUpperCase();
    return ("00000000000000000000000000000000" + hexId).slice(-32);
}

export function generateDeterministicTemplateIdFromTemplateIdAndStringKey(
    baseTemplateId: string,
    stringKey: string,
): string {
    const hash = generateStableHash128(stringKey);
    const hexId = (hexStringToBigInt(baseTemplateId) ^ hash).toString(16).toUpperCase();
    return ("00000000000000000000000000000000" + hexId).slice(-32);
}

//------------------------------------------------------------------------------
/**
 * If you want to create a new entity with a templateId do:
 *  * ```
 * let templateId = makeUUID();
 * let entityId = generateDeterministicId(projectId, templateId, "uuid")
 * let entity = entityContext.createEntity("FormType", entityId)
 * ```
 *
 * If you want to add a templateId to an existing entity do:
 * ```
 * existingEntity.templateId = generateDeterministicTemplateId(projectId, entity.id);
 * ```
 *
 * NEVER USE:
 * ```
 * existingEntity.templateId = makeUUID();
 * ```
 */

/**
 * Generate a new UUID
 */
export function makeUUID() {
    const uuid =
        randHex(0xffff) +
        randHex(0xffff) +
        randHex(0xffff) +
        randHex(0xffff) +
        randHex(0xffff) +
        randHex(0xffff) +
        randHex(0xffff) +
        randHex(0xffff);
    return uuid.toUpperCase();
}

/**
 * Generate a templateId from a baseDeterministicId and an existing entityId.
 */
export function generateDeterministicTemplateId(baseDeterministicId: string, entityId: string): string {
    if (typeof baseDeterministicId !== "string" || !baseDeterministicId.match(/^[0-9]+$/))
        throw new IllegalArgumentError(`Invalid baseDeterministicId key: ${baseDeterministicId}`);
    if (typeof entityId !== "string" || !entityId.match(/^[0-9]+$/))
        throw new IllegalArgumentError("Invalid entityId key", "entityId");

    const baseId = BigInt(baseDeterministicId);
    const keyId = BigInt(entityId);

    let hexId = (baseId ^ keyId).toString(16).toUpperCase();
    hexId = ("00000000000000000000000000000000" + hexId).slice(-32);
    return hexId;
}

export function generateUUIDFromStringKey(stringKey: string): string {
    const hash = SHA512(stringKey).toString();
    const hex = hash.toUpperCase().slice(0, 32);
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}
